import { MaskedInput } from '@alfa-retail-react/components';
import React, { ChangeEvent, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { observer } from 'mobx-react';
import Select from '@alfa-retail-react/components/select';
import { useFormContext } from '@alfa-retail-react/components/form-hook';

import { WEB_VISOR_DISABLE } from 'app/utils/AnalyticsUtils';
import { InputFieldEventHandler } from 'app/form/InputFieldEventHandler';
import { FieldSet } from 'app/component/fieldset/FieldSet';
import { EmploymentFormValues } from 'app/logic/pages/employment/EmploymentPage';
import { EmploymentFormStore } from 'app/logic/pages/employment/EmploymentFormStore';
import { useIsDesktop } from 'app/utils/useWindowType';
import { ValidationRules } from 'app/form/ValidationRules';
import { transliterationMapper } from 'app/utils/StringUtils';

export interface SoleOwnerFormProps {
    /**
     * Хранилище состояния формы ввода сведений о работе.
     */
    formStore: EmploymentFormStore;
}

const UNP_MASK_WITH_LETTERS = [
    /[A-Za-zА-Яа-я\d]/,
    /[A-Za-zА-Яа-я\d]/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
];
const UNP_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/];

/**
 * Добавляет предобработку введенного значения к полю УНП
 * @param onChangeHandler Функция-обработчик события onChange
 * @returns Функцию-обработчик события onChange с замененными на верхний регистр символами и транслитерированными соответствующими из латиницы
 */
const withUpperCaseAndTransliteration =
    (onChangeHandler: (event: ChangeEvent<HTMLInputElement>) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.value || event.target.value.length === 0) {
            return '';
        }

        const upperCaseValue = event.target.value.toUpperCase();
        const transliteratedValue = upperCaseValue
            .split('')
            .map(transliterationMapper)
            .join('');

        onChangeHandler({
            ...event,
            target: { ...event.target, value: transliteratedValue },
        });
    };

/**
 * Набор полей отвечающий за выбора вида деятельности пользователя
 */
export const SoleOwnerForm = observer(({ formStore }: SoleOwnerFormProps) => {
    const { control, trigger, handleMultipleErrors, setFieldElement } =
        useFormContext<EmploymentFormValues>();
    const fieldHandler = new InputFieldEventHandler<EmploymentFormValues>(
        trigger,
    );

    const [isDesktop] = useIsDesktop();

    const unpMask = formStore.isSelfEmployed ? UNP_MASK_WITH_LETTERS : UNP_MASK;
    const unpHint = 'Учётный номер налогоплательщика (9 цифр)';

    const unpPattern = formStore.isSelfEmployed
        ? ValidationRules.unpSelfEmployed
        : ValidationRules.unp;

    const unpInputType = formStore.isSelfEmployed ? 'text' : 'number';
    const unpPlaceholder = formStore.isSelfEmployed
        ? ValidationRules.unpMessage
        : '123456789';

    return (
        <FieldSet
            className="soleOwnerFormSection"
            fieldsProps={[
                {
                    className: 'activityType',
                    title: '',
                    value: (
                        <Controller<EmploymentFormValues>
                            control={control}
                            defaultValue={formStore.activityKindCode}
                            name={'activityKindCode'}
                            rules={{ required: ValidationRules.required() }}
                            render={({ field, fieldState }) => {
                                return (
                                    <Select
                                        componentRef={(element) => {
                                            field.ref(element);
                                            setFieldElement(
                                                'activityKindCode',
                                                element,
                                            );
                                        }}
                                        isDesktop={isDesktop}
                                        size="l"
                                        options={formStore.employmentReference}
                                        selected={String(field.value)}
                                        error={handleMultipleErrors(
                                            fieldState.error,
                                        )}
                                        label={'Вид деятельности'}
                                        onBlur={field.onBlur}
                                        onChange={(event) => {
                                            field.onChange(event.selected.key);
                                            formStore.setActivityKindCode(
                                                event.selected.key,
                                            );
                                        }}
                                    />
                                );
                            }}
                        />
                    ),
                },
                {
                    className: 'unp',
                    title: '',
                    value: (
                        <Controller<EmploymentFormValues>
                            defaultValue={formStore.unp ?? ''}
                            control={control}
                            name={'unp'}
                            rules={{
                                required: ValidationRules.required(),
                                minLength: ValidationRules.minLength(
                                    9,
                                    ValidationRules.unpMessage,
                                ),
                                pattern: unpPattern,
                            }}
                            render={({ field, fieldState }) => {
                                const { onBlur, onClear, onChange } =
                                    fieldHandler.getEventHandlers(
                                        field,
                                        'unp',
                                        formStore.setUnp,
                                        false,
                                    );

                                const unpOnChangeHandler =
                                    withUpperCaseAndTransliteration(onChange);

                                /**
                                 * Воркэраунд для обновления значения в неконтролируемом компоненте Controller'а
                                 */
                                useEffect(() => {
                                    const isValueOutdated =
                                        field.value !== formStore.unp;

                                    if (isValueOutdated) {
                                        field.onChange(formStore.unp);
                                        trigger(field.name);
                                    }
                                }, [formStore.unp]);

                                return (
                                    <MaskedInput
                                        clear
                                        inputClassName={WEB_VISOR_DISABLE}
                                        ref={(element) => {
                                            field.ref(element);
                                            setFieldElement('unp', element);
                                        }}
                                        placeholder={unpPlaceholder}
                                        type={unpInputType}
                                        mask={unpMask}
                                        value={String(field.value)}
                                        size={'l'}
                                        error={handleMultipleErrors(
                                            fieldState.error,
                                        )}
                                        label={'УНП'}
                                        hint={unpHint}
                                        onBlur={onBlur}
                                        onClear={onClear}
                                        onChange={unpOnChangeHandler}
                                    />
                                );
                            }}
                        />
                    ),
                    isVisible: formStore.availableUNP,
                },
            ]}
        />
    );
});
